<template>
  <ActionPanelShell
    :title="selectedItem ? 'Form link and QR' : 'Form QR code'"
    :subtitle="subtitle"
    :show-back="!!selectedItem"
    @close="close"
    @back="onBack"
  >
    <div class="sp qp" data-test="form-qr-panel">
      <!-- ===== Step 1 — pick a form or packet ===== -->
      <template v-if="!selectedItem">
        <div class="qp-search" data-test="qr-search">
          <div class="qp-search__wrap">
            <i class="fa fa-search qp-search__ic" aria-hidden="true"></i>
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="sp-input qp-search__input"
              autocomplete="off"
              placeholder="Search forms and packets"
              aria-label="Search forms and packets"
              data-test="qr-search-input"
            >
          </div>
        </div>

        <div v-if="loading" class="sp-skel" aria-hidden="true" data-test="qr-loading"><i></i><i></i><i></i><i></i></div>

        <p v-else-if="combinedItems.length === 0" class="qp-empty" data-test="qr-empty">No forms available. Create a form on the Teamio Forms page first.</p>

        <!-- Searching: one flat list. -->
        <template v-else-if="searchActive">
          <p v-if="searchResults.length === 0" class="qp-empty" data-test="qr-no-match">No forms match “{{ searchQuery }}”.</p>
          <div v-else class="qp-sec">
            <div class="qp-sec__h"><i class="fa fa-search" aria-hidden="true"></i> Results <span class="qp-sec__n">{{ searchResults.length }}</span></div>
            <button v-for="item in searchResults" :key="item._id" type="button" class="qp-row" data-test="qr-form-row" @click="selectItem(item)">
              <i :class="item._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" class="qp-row__ic" aria-hidden="true"></i>
              <span class="qp-row__name">{{ item.Name }}</span>
              <span v-if="item._isPacket" class="qp-badge qp-badge--blue">Packet</span>
              <span v-for="lang in getFormTranslationLanguages(item)" :key="lang" class="qp-badge qp-badge--amber">{{ lang.toUpperCase() }}</span>
              <i class="fa fa-chevron-right qp-row__chev" aria-hidden="true"></i>
            </button>
          </div>
        </template>

        <template v-else>
          <!-- Today's visit: the consent for the actual work, each row's QR prefilling only its own teeth. -->
          <div v-if="activeAppointment" class="qp-visit" :class="{ 'is-completed': activeAppointment.isRecentlyCompleted }" data-test="qr-visit">
            <div class="qp-visit__h">
              <i class="fa fa-stethoscope" aria-hidden="true"></i>
              <span class="qp-visit__t" data-test="qr-visit-title">{{ visitTitle }}</span>
              <span class="qp-visit__n">· {{ procedureCount }} procedure{{ procedureCount === 1 ? '' : 's' }}</span>
              <span class="qp-visit__when">{{ formatAptDate(activeAppointment) }}</span>
            </div>

            <div v-if="appointments.length > 1" class="qp-apts" data-test="qr-apt-switcher">
              <button
                v-for="(apt, idx) in appointments"
                :key="apt.aptNum"
                type="button"
                class="qp-apt"
                :class="{ 'is-on': idx === selectedAptIndex }"
                @click="selectedAptIndex = idx"
              >{{ formatAptDate(apt) }}<i v-if="apt.isRecentlyCompleted" class="fa fa-check" title="Completed" aria-hidden="true"></i></button>
            </div>

            <button
              v-for="group in activeAppointment.groups"
              :key="'g-' + group.formIDForWeb"
              type="button"
              class="qp-proc"
              data-test="qr-proc-mapped"
              @click="selectProcedureGroup(group)"
            >
              <span class="qp-proc__body">
                <span class="qp-proc__codes">
                  <span v-for="code in group.codes" :key="code.procCode" class="qp-code">{{ code.procCode }}<template v-if="code.count > 1"> ×{{ code.count }}</template></span>
                  <span class="qp-proc__desc">{{ groupDescription(group) }}</span>
                </span>
                <span class="qp-proc__form"><i class="fa fa-shield" aria-hidden="true"></i> {{ group.formName }}</span>
                <span v-if="group.teeth" class="qp-proc__teeth">Teeth {{ group.teeth }}</span>
              </span>
              <i class="fa fa-chevron-right qp-row__chev" aria-hidden="true"></i>
            </button>

            <!-- Unmapped: shown, not hidden, so the gap is visible and fixable. -->
            <div v-for="item in activeAppointment.unmapped" :key="'u-' + item.procCode" class="qp-proc qp-proc--none" data-test="qr-proc-unmapped">
              <span class="qp-proc__body">
                <span class="qp-proc__codes">
                  <span class="qp-code qp-code--muted">{{ item.procCode }}<template v-if="item.count > 1"> ×{{ item.count }}</template></span>
                  <span class="qp-proc__desc">{{ item.descript }}</span>
                </span>
                <span class="qp-proc__none">No consent form mapped</span>
              </span>
            </div>

            <p v-if="!mappingsConfigured" class="qp-visit__hint">Map procedure codes to consent forms in Forms and the right one shows up here.</p>
          </div>

          <div v-if="activeAppointment" class="qp-or"><span>Or pick any form</span></div>

          <div v-if="consentItems.length" class="qp-sec" data-test="qr-consent">
            <div class="qp-sec__h"><i class="fa fa-shield" aria-hidden="true"></i> Consent forms</div>
            <button v-for="item in consentItems" :key="item._id" type="button" class="qp-row" data-test="qr-form-row" @click="selectItem(item)">
              <i :class="item._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" class="qp-row__ic" aria-hidden="true"></i>
              <span class="qp-row__name">{{ item.Name }}</span>
              <span v-if="item._isPacket" class="qp-badge qp-badge--blue">Packet</span>
              <span v-for="lang in getFormTranslationLanguages(item)" :key="lang" class="qp-badge qp-badge--amber">{{ lang.toUpperCase() }}</span>
              <i class="fa fa-chevron-right qp-row__chev" aria-hidden="true"></i>
            </button>
          </div>

          <div v-if="pinnedItems.length" class="qp-sec" data-test="qr-pinned">
            <div class="qp-sec__h"><i class="fa fa-thumb-tack" aria-hidden="true"></i> Pinned</div>
            <button v-for="item in pinnedItems" :key="item._id" type="button" class="qp-row" data-test="qr-form-row" @click="selectItem(item)">
              <i :class="item._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" class="qp-row__ic" aria-hidden="true"></i>
              <span class="qp-row__name">{{ item.Name }}</span>
              <span v-if="item._isPacket" class="qp-badge qp-badge--blue">Packet</span>
              <span v-for="lang in getFormTranslationLanguages(item)" :key="lang" class="qp-badge qp-badge--amber">{{ lang.toUpperCase() }}</span>
              <i class="fa fa-chevron-right qp-row__chev" aria-hidden="true"></i>
            </button>
          </div>

          <div v-if="allItems.length" class="qp-sec" data-test="qr-all">
            <div class="qp-sec__h"><i class="fa fa-file-text-o" aria-hidden="true"></i> All forms <span class="qp-sec__n">{{ allItemsRangeLabel }}</span></div>
            <button v-for="item in paginatedAllItems" :key="item._id" type="button" class="qp-row" data-test="qr-form-row" @click="selectItem(item)">
              <i :class="item._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" class="qp-row__ic" aria-hidden="true"></i>
              <span class="qp-row__name">{{ item.Name }}</span>
              <span v-if="item._isPacket" class="qp-badge qp-badge--blue">Packet</span>
              <span v-for="lang in getFormTranslationLanguages(item)" :key="lang" class="qp-badge qp-badge--amber">{{ lang.toUpperCase() }}</span>
              <i class="fa fa-chevron-right qp-row__chev" aria-hidden="true"></i>
            </button>
            <div v-if="allItemsTotalPages > 1" class="qp-pager" data-test="qr-pager">
              <button type="button" class="qp-pager__btn" aria-label="Previous page" :disabled="allItemsPage === 1" data-test="qr-prev" @click="allItemsPage--"><i class="fa fa-angle-left" aria-hidden="true"></i></button>
              <span class="qp-pager__n">Page {{ allItemsPage }} of {{ allItemsTotalPages }}</span>
              <button type="button" class="qp-pager__btn" aria-label="Next page" :disabled="allItemsPage === allItemsTotalPages" data-test="qr-next" @click="allItemsPage++"><i class="fa fa-angle-right" aria-hidden="true"></i></button>
            </div>
          </div>
        </template>
      </template>

      <!-- ===== Step 2 — the code, and the link to copy ===== -->
      <section v-else class="sp-sec qp-code-step" data-test="qr-code-step">
        <div class="qp-name" data-test="qr-form-name">
          {{ selectedItem.Name }}
          <span v-if="selectedItem._isPacket" class="qp-badge qp-badge--blue">Packet</span>
        </div>

        <!-- Confirms what this QR will prefill, so a wrong pick is caught before the tablet is handed over. -->
        <div v-if="procScope" class="qp-scope" data-test="qr-scope">
          <i class="fa fa-check-circle" aria-hidden="true"></i>
          Prefills teeth <strong>{{ procScope.teeth || 'none' }}</strong>
          <span class="qp-scope__codes">({{ procScope.codeLabel }})</span>
        </div>

        <div v-if="qrLoading" class="qp-wait" data-test="qr-generating">
          <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          <p>Generating QR code…</p>
        </div>

        <template v-else-if="qrError">
          <PanelNotice tone="error" data-test="qr-error">{{ qrError }}</PanelNotice>
          <div><button type="button" class="sp-btn-secondary" data-test="qr-retry" @click="generateUrlAndQR">Try again</button></div>
        </template>

        <template v-else>
          <div class="qp-canvas"><canvas ref="qrCanvas" data-test="qr-canvas"></canvas></div>
          <p class="qp-hint">Have {{ firstName }} scan it with their phone or the office tablet, or copy the link.</p>
          <div class="qp-link" data-test="qr-link-row">
            <div class="qp-link__wrap">
              <i class="fa fa-link qp-search__ic" aria-hidden="true"></i>
              <input
                ref="linkInput"
                type="text"
                class="sp-input qp-search__input"
                readonly
                :value="shortUrl"
                aria-label="Form link"
                data-test="qr-link"
                @click="$refs.linkInput.select()"
              >
            </div>
            <button type="button" class="sp-btn-secondary qp-copy" data-test="qr-copy" @click="copyLink">
              <i class="fa fa-copy" aria-hidden="true"></i> Copy
            </button>
          </div>
        </template>
      </section>
    </div>

    <template v-if="selectedItem" #footer>
      <button type="button" class="qp-foot-btn ap-foot-main" data-test="qr-done" @click="close">Done</button>
    </template>
  </ActionPanelShell>
</template>

<script>
/**
 * Form QR code — the redesigned panel (design "Patient Action Panels" 2i, 3a), opened by
 * PatientPanelActionHost.run('qr') through openActionPanel() (480px, 'ap-slideout').
 *
 * `extends` GetFormQRModal: the forms/packets load, today's-visit procedure groups, the procedure scope
 * appended to the link (?aptNum=&codes=), the short-URL request (getFormUrl / getPacketUrl) and the QR
 * render are all the classic code. Only the shell is new:
 *   - TODAY'S VISIT on top (the consent for the actual work), then "Or pick any form";
 *   - PICKING a form moves to the CODE step, with a back arrow in the header (not a "Back to forms" link);
 *   - COPY the link in one click (the classic copyLink, with its toast).
 *
 * ⛔ Keeps the classic refs: `searchInput` (onShow focuses it), `linkInput` (its click selects) and
 * `qrCanvas` (renderQR draws into it). The canvas exists only once the link is ready, as before.
 *
 * Closing was `$emit('input', false)` (b-modal v-model); here it is `closePanel`, the one event the
 * slide-out library binds. onShow() — which b-modal's @show used to call — runs when the panel mounts.
 *
 * The classic modal has no mixin.js, so its `patientNum` / `siteId` / `timezone` / `patientData` props
 * are inherited as they are.
 */
import GetFormQRModal from '@/pages/PatientProfile/components/GetFormQRModal';
import ActionPanelShell from './ActionPanelShell.vue';
import PanelNotice from './PanelNotice.vue';

export default {
  name: 'FormQrPanel',

  extends: GetFormQRModal,

  components: { ActionPanelShell, PanelNotice },

  computed: {
    patientFullName() {
      const p = this.patientData || {};
      return [p.FName, p.LName].filter(Boolean).join(' ').trim();
    },
    firstName() {
      return (this.patientData && this.patientData.FName) || 'the patient';
    },
    subtitle() {
      if (!this.patientFullName) return '';
      return this.patientNum != null && this.patientNum !== '' ? `For ${this.patientFullName} · ID ${this.patientNum}` : `For ${this.patientFullName}`;
    },
    /** "Today's visit", or the classic headline for an upcoming / recently completed one. */
    visitTitle() {
      const apt = this.activeAppointment;
      if (apt && !apt.isRecentlyCompleted && this.isToday(apt)) return 'Today’s visit';
      return this.appointmentHeadline;
    },
  },

  mounted() {
    // What b-modal's @show did: reset, load the forms and the visit, focus the search.
    this.onShow();
  },

  methods: {
    close() {
      this.$emit('closePanel', {});
    },

    /** The header's back arrow (the classic "Back to forms" link was disabled while the code loaded). */
    onBack() {
      if (this.qrLoading) return;
      this.backToList();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;

.qp-foot-btn { @include ap-btn-primary; }

// Search — sticky under the header.
.qp-search {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 16px $ap-gutter 14px;
  border-bottom: 1px solid $pp-line;
  background: $pp-surface;

  @include ap-phone { padding-left: $ap-gutter-phone; padding-right: $ap-gutter-phone; }
}

.qp-search__wrap,
.qp-link__wrap { position: relative; flex: 1 1 auto; min-width: 0; }

.qp-search__ic {
  position: absolute;
  top: 50%;
  left: 13px;
  transform: translateY(-50%);
  font-size: 13px;
  color: $pp-ink-4;
  pointer-events: none;
}

.qp-search__input { padding-left: 36px; }

.qp-empty { margin: 0; padding: 28px $ap-gutter; text-align: center; font-size: 13.5px; color: $pp-ink-3; }

// Sections of forms.
.qp-sec { padding: 4px 12px 8px; }

.qp-sec__h {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px 12px 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $pp-ink-3;

  i { color: $pp-ink-4; }
}

.qp-sec__n { margin-left: auto; letter-spacing: 0; text-transform: none; font-weight: 600; color: $pp-ink-4; }

.qp-row {
  @include ap-row;
  width: 100%;
  border-color: transparent;
  font: inherit;
  font-size: 14px;

  &:hover { background: $pp-bg; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: -2px; }
}

.qp-row__ic { flex: none; color: $pp-ink-4; }
.qp-row__name { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.qp-row__chev { flex: none; font-size: 12px; color: $neutral-300; }

.qp-badge {
  flex: none;
  padding: 2px 8px;
  border-radius: $pp-radius-pill;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.qp-badge--blue { background: $pp-blue-bg; color: $pp-blue-ink; }
.qp-badge--amber { background: $pp-amber-bg; color: $pp-amber-ink; }

.qp-pager { display: flex; align-items: center; justify-content: flex-end; gap: 6px; padding: 6px 12px 0; font-size: 12.5px; color: $pp-ink-3; }

.qp-pager__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: $pp-radius;
  background: transparent;
  color: $pp-ink-2;
  font-size: 16px;
  cursor: pointer;

  &:hover:not(:disabled) { background: $pp-bg; }
  &:disabled { color: $neutral-300; cursor: not-allowed; }

  @include ap-touch { width: 44px; height: 44px; }
}

// Today's visit.
.qp-visit {
  margin: 16px $ap-gutter 0;
  overflow: hidden;
  border: 1px solid $pp-line;
  border-radius: $pp-radius-lg;

  @include ap-phone { margin-left: $ap-gutter-phone; margin-right: $ap-gutter-phone; }
}

.qp-visit__h {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  padding: 12px 14px;
  border-bottom: 1px solid $pp-line;
  background: $pp-bg-2;
  font-size: 13.5px;

  > i { color: $pp-brand-deep; }
}

.qp-visit__t { font-weight: 800; }
.qp-visit__n { color: $pp-ink-3; }
.qp-visit__when { margin-left: auto; font-size: 13px; font-weight: 600; color: $pp-ink-2; }

.qp-apts { display: flex; flex-wrap: wrap; gap: 6px; padding: 10px 14px 0; }

.qp-apt {
  min-height: 30px;
  padding: 0 11px;
  border: 1px solid $pp-line;
  border-radius: $pp-radius-pill;
  background: $pp-surface;
  color: $pp-ink-2;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &.is-on { border-color: $pp-brand-deep; background: $pp-brand-soft; color: $pp-brand-deep; }
  i { margin-left: 6px; }

  @include ap-touch { min-height: 40px; }
}

.qp-proc {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 0;
  border-top: 1px solid $pp-line-2;
  background: $pp-surface;
  color: $pp-ink;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:first-of-type { border-top: 0; }
  &:hover { background: $pp-bg-2; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: -2px; }

  @include ap-touch { min-height: $ap-row-h-touch; }
}

.qp-proc--none { background: $pp-bg-2; cursor: default; &:hover { background: $pp-bg-2; } }

.qp-proc__body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.qp-proc__codes { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; }

.qp-code {
  padding: 2px 6px;
  border-radius: 5px;
  background: $pp-line-2;
  font-family: $pp-mono;
  font-size: 12px;
  font-weight: 600;
}

.qp-code--muted { color: $pp-ink-3; }
.qp-proc__desc { font-size: 13px; color: $pp-ink-3; }
.qp-proc__form { display: flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 700; i { color: $pp-brand-deep; } }
.qp-proc__teeth { font-size: 12.5px; color: $pp-ink-3; }
.qp-proc__none { font-size: 13px; font-style: italic; color: $pp-ink-3; }

.qp-visit__hint {
  margin: 0;
  padding: 10px 14px;
  border-top: 1px solid $pp-line;
  font-size: 12.5px;
  line-height: 1.45;
  color: $pp-ink-3;
}

.qp-or {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px $ap-gutter 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $pp-ink-4;

  &::before,
  &::after { content: ''; flex: 1; height: 1px; background: $pp-line; }

  @include ap-phone { margin-left: $ap-gutter-phone; margin-right: $ap-gutter-phone; }
}

// Step 2 — the code.
.qp-code-step { align-items: center; gap: 14px; padding-top: 28px; text-align: center; }

.qp-name {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.qp-scope {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px 6px;
  padding: 5px 11px;
  border-radius: $pp-radius-pill;
  background: $pp-brand-soft;
  color: $teal-800;
  font-size: 12.5px;
  font-weight: 600;
}

.qp-scope__codes { font-weight: 500; }

.qp-wait {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: $pp-ink-3;

  i { font-size: 24px; }
  p { margin: 0; font-size: 13.5px; }
}

.qp-canvas {
  padding: 18px;
  border: 1px solid $pp-line;
  border-radius: 16px;
  background: #fff;
  box-shadow: $pp-shadow;

  canvas { display: block; max-width: 100%; height: auto !important; }
}

.qp-hint { max-width: 340px; margin: 0; font-size: 13.5px; line-height: 1.5; color: $pp-ink-2; }

.qp-link { display: flex; gap: 8px; width: 100%; text-align: left; }
.qp-copy { flex: none; }
</style>
